/**
 * News & Communication Help Content
 *
 * Covers news feed, creating posts, engagement, and department news.
 */

import type { HelpContent } from '../types';
import {
  createHelpContent,
  createSection,
  createFAQ,
  createTip,
  createRelatedLink,
} from '../utils';

// =============================================================================
// MAIN NEWS HELP
// =============================================================================

export const newsHelp: HelpContent = createHelpContent({
  id: 'news',
  title: 'News & Communication',
  description: 'Company announcements, updates, and internal communication',
  lastUpdated: '2026-02-08',
  version: '2.0',

  tips: [
    createTip(
      'tip-featured',
      'info',
      'Featured Posts',
      'Important news is marked as "Featured" and pinned to the top of the feed. Check featured posts first for critical updates.'
    ),
  ],

  sections: [
    createSection(
      'overview',
      'Overview',
      `<p>The News page is your hub for company announcements, updates, and internal communication. Stay informed about organizational changes, team achievements, policy updates, and important events.</p>
      <p>Key features:</p>
      <ul>
        <li><strong>News Feed</strong> — Chronological list of company updates</li>
        <li><strong>Featured Posts</strong> — Important announcements pinned to the top</li>
        <li><strong>Categories</strong> — Filter by announcement type</li>
        <li><strong>Engagement</strong> — React, comment, and bookmark posts</li>
      </ul>`
    ),
    createSection(
      'browsing',
      'News Feed',
      `<p>News articles are displayed in chronological order with the most recent first. Each article shows:</p>
      <ul>
        <li><strong>Title and Summary</strong> — A quick overview of the content</li>
        <li><strong>Author</strong> — Who published the article</li>
        <li><strong>Date</strong> — When it was published</li>
        <li><strong>Category</strong> — The type of news (announcement, update, event, etc.)</li>
      </ul>
      <p>Click on any article to read the full content.</p>`
    ),
    createSection(
      'creating',
      'Creating News Posts',
      `<p>If you have permission to create news posts:</p>
      <ul>
        <li>Click "Create Post" to open the editor</li>
        <li>Write a clear, descriptive title</li>
        <li>Add a summary that captures the key message</li>
        <li>Include all relevant details in the body (supports rich text formatting)</li>
        <li>Select the appropriate category</li>
        <li>Choose whether to feature/pin the post</li>
        <li>Select target audience (all, specific departments, etc.)</li>
      </ul>`,
    ),
    createSection(
      'engagement',
      'Engaging with Posts',
      `<p>You can interact with news posts in several ways:</p>
      <ul>
        <li><strong>Reactions</strong> — Express your response with emoji reactions</li>
        <li><strong>Comments</strong> — Share your thoughts and ask questions</li>
        <li><strong>Bookmarks</strong> — Save posts to read later</li>
        <li><strong>Share</strong> — Share posts with specific colleagues</li>
      </ul>`
    ),
    createSection(
      'department-news',
      'Department & Team News',
      '<p>Posts can be targeted to specific departments or teams. Use the category filter to view posts relevant to your department. Department-specific announcements help reduce noise and ensure relevant updates reach the right people.</p>'
    ),
  ],

  faqs: [
    createFAQ(
      'faq-create-permissions',
      'Who can create news posts?',
      'Team leads, managers, HR personnel, and administrators can create news posts. If you need to share an important announcement, contact your team lead or HR department.',
      ['permissions', 'create', 'roles', 'publish', 'who']
    ),
    createFAQ(
      'faq-notifications',
      'How do I get notified about important news?',
      'Important news is marked as "Featured" and appears at the top of the feed. You\'ll also receive email notifications for critical announcements based on your notification settings.',
      ['notifications', 'featured', 'email', 'alerts']
    ),
    createFAQ(
      'faq-edit-delete',
      'Can I edit or delete my post after publishing?',
      'Yes, you can edit your own posts by clicking the edit button. Posts can also be deleted, but use this carefully as it removes the post for everyone. Consider editing instead of deleting.',
      ['edit', 'delete', 'modify', 'post', 'published']
    ),
    createFAQ(
      'faq-filter',
      'How do I filter news by category?',
      'Use the category filter buttons at the top of the news feed to view posts from specific categories like Announcements, Updates, Events, or HR Communications.',
      ['filter', 'category', 'sort', 'view', 'type']
    ),
    createFAQ(
      'faq-older',
      'Where can I find older news articles?',
      'Scroll down the news feed to load older articles, or use the search functionality to find specific past announcements by keyword.',
      ['archive', 'history', 'old', 'search', 'past']
    ),
    createFAQ(
      'faq-pin',
      'How do I pin an important announcement?',
      'When creating or editing a post, toggle the "Featured" option to pin it to the top of the feed. Only admins and managers can feature posts.',
      ['pin', 'featured', 'top', 'important', 'sticky']
    ),
  ],

  relatedLinks: [
    createRelatedLink('Dashboard', '/', { description: 'Return to your dashboard overview' }),
    createRelatedLink('Directory', '/directory', { description: 'Find colleagues mentioned in news' }),
    createRelatedLink('Knowledge Base', '/knowledge', { description: 'Find detailed documentation and policies' }),
  ],

  keywords: [
    'news',
    'announcements',
    'posts',
    'updates',
    'communication',
    'articles',
    'company news',
    'featured',
    'department',
  ],
});
